import { useEffect, useRef, useState } from "react";
import { getOverallRiskClass } from "@/lib/utils";
import { cn } from "@/lib/utils";

interface SummaryCardProps {
  summary: string;
  overallRiskScore: number;
}

export function SummaryCard({ summary, overallRiskScore }: SummaryCardProps) {
  const riskClass = getOverallRiskClass(overallRiskScore);
  const textRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [canTruncate, setCanTruncate] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [summary]);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const measure = () => {
      if (expanded) return;
      setCanTruncate(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [summary, expanded]);

  const showToggle = canTruncate || expanded;

  return (
    <div className="mb-6 rounded-xl border border-[var(--summary-card-border)] bg-[var(--summary-card-bg)] p-4 shadow-[var(--shadow-card)] dark:border-indigo-300/15 dark:bg-slate-950/40 dark:shadow-none">
      <div
        ref={textRef}
        className={cn(
          "text-sm text-[var(--lm-foreground)] dark:text-inherit",
          !expanded && "line-clamp-4"
        )}
      >
        {summary}
      </div>
      {showToggle ? (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-2 text-sm font-medium text-primary hover:underline dark:text-violet-300"
        >
          {expanded ? "View less" : "View more"}
        </button>
      ) : null}
      <div className="mt-3">
        <strong className="text-[var(--lm-foreground-strong)] dark:text-inherit">Overall Risk Score:</strong>
        <span
          className={cn("ml-2 inline-block rounded-full px-3.5 py-1 text-sm font-medium", riskClass)}
        >
          {overallRiskScore}/10
        </span>
      </div>
    </div>
  );
}
